import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Btn, Card, Modal, Input, Select, Textarea } from '../shared/UI';
import codes from '../../data/einvoiceCodes.json';
import { useT } from '../../i18n';
import api from '../../utils/api';

const COLORS = { none: '#94a3b8', submitted: '#F59E0B', valid: '#10B981', invalid: '#EF4444', cancelled: '#94a3b8' };
export const E_INVOICE_TYPES = ['invoice', 'credit_note'];
export const isEInvoiceLocked = doc => ['submitted', 'valid'].includes(doc?.einvoice?.status);

// LHDN e-Invoice status for one document, with submit / check / cancel.
// Hidden entirely until the user has switched e-Invoicing on (or the document already has a history).
export default function EInvoiceCard({ doc, onDocChange, show }) {
  const { t } = useT();
  const navigate = useNavigate();
  const [enabled, setEnabled] = useState(false);
  const [modal, setModal] = useState('');          // '' | 'submit' | 'cancel'
  const [ready, setReady] = useState(null);         // readiness response
  const [classificationCode, setClassificationCode] = useState('022');
  const [exchangeRate, setExchangeRate] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState('');

  const ei = doc.einvoice || {};
  const status = ei.status || 'none';
  const foreign = (doc.currency || 'MYR') !== 'MYR';

  useEffect(() => {
    api.get('/einvoice/settings').then(r => setEnabled(!!r.data.enabled)).catch(() => {});
  }, []);

  const loadReadiness = rate => api.get(`/einvoice/documents/${doc._id}/readiness${rate ? `?exchangeRate=${encodeURIComponent(rate)}` : ''}`)
    .then(r => { setReady(r.data); return r.data; });

  const openSubmit = async () => {
    setModal('submit'); setReady(null);
    try { const r = await loadReadiness(exchangeRate); setClassificationCode(r.defaultClassification || '022'); }
    catch (err) { show(err.response?.data?.message || t('einv.failed'), 'error'); setModal(''); }
  };

  const submit = async () => {
    setBusy('submit');
    try {
      const { data } = await api.post(`/einvoice/documents/${doc._id}/submit`, { classificationCode, exchangeRate: foreign ? Number(exchangeRate) : undefined });
      onDocChange(data);
      setModal('');
      const s = data.einvoice?.status;
      show(t(`einv.done_${s === 'valid' || s === 'invalid' ? s : 'submitted'}`), s === 'invalid' ? 'error' : s === 'valid' ? 'success' : 'warning');
    } catch (err) {
      const d = err.response?.data;
      if (d?.code === 'einvNotReady' && Array.isArray(d.details)) setReady(r => ({ ...r, problems: d.details }));
      else show([d?.message || t('einv.failed'), ...(Array.isArray(d?.details) ? d.details.slice(0, 2) : [])].join(' — '), 'error');
    } finally { setBusy(''); }
  };

  const refresh = async () => {
    setBusy('refresh');
    try { const { data } = await api.post(`/einvoice/documents/${doc._id}/refresh`); onDocChange(data); }
    catch (err) { show(err.response?.data?.message || t('einv.failed'), 'error'); }
    finally { setBusy(''); }
  };

  const cancel = async e => {
    e.preventDefault();
    setBusy('cancel');
    try {
      const { data } = await api.post(`/einvoice/documents/${doc._id}/cancel`, { reason });
      onDocChange(data); setModal(''); setReason('');
      show(t('einv.cancelled'));
    } catch (err) { show(err.response?.data?.message || t('einv.failed'), 'error'); }
    finally { setBusy(''); }
  };

  if (!E_INVOICE_TYPES.includes(doc.type)) return null;
  if (!enabled && status === 'none') return null;

  const problems = ready?.problems || [];
  const needsRate = foreign && !(Number(exchangeRate) > 0);
  const blocking = problems.filter(p => !(p.code === 'einvExchangeRate')); // the rate is fixed right here in the dialog
  const fixSettings = blocking.some(p => p.field.startsWith('settings'));
  const fixClient = blocking.some(p => p.field.startsWith('client'));

  return (
    <Card>
      <p style={label}>{t('einv.card')}{ei.environment === 'sandbox' && <span style={sandboxTag}>{t('einv.sandboxBadge')}</span>}</p>
      <div style={{ fontSize: 13.5, fontWeight: 600, color: COLORS[status] }}>● {t(`einv.st_${status}`)}</div>

      {ei.uuid && <div style={meta} dir="ltr"><b>{t('einv.uuid')}:</b> {ei.uuid}</div>}
      {status === 'valid' && ei.validatedAt && <div style={meta}>{t('einv.validatedOn', { date: new Date(ei.validatedAt).toLocaleString() })}</div>}
      {status === 'invalid' && ei.problems?.length > 0 && (
        <ul style={{ margin: '8px 0 0', paddingInlineStart: 18, fontSize: 12.5, color: 'var(--danger)', lineHeight: 1.6 }}>
          {ei.problems.slice(0, 8).map((p, i) => <li key={i} style={{ overflowWrap: 'anywhere' }}>{p}</li>)}
        </ul>
      )}
      {status === 'cancelled' && ei.cancelReason && <div style={meta}>{ei.cancelReason}</div>}
      {isEInvoiceLocked(doc) && <p style={{ fontSize: 12, color: 'var(--text-subtle)', marginTop: 8, lineHeight: 1.5 }}>🔒 {t('einv.locked')}</p>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
        {['none', 'invalid', 'cancelled'].includes(status) && enabled && (
          <Btn variant="primary" size="sm" onClick={openSubmit} style={full}>🏛 {status === 'invalid' ? t('einv.resubmit') : t('einv.submit')}</Btn>
        )}
        {status === 'submitted' && <Btn variant="outline" size="sm" onClick={refresh} disabled={busy === 'refresh'} style={full}>↻ {t('einv.refresh')}</Btn>}
        {status === 'valid' && ei.url && <a href={ei.url} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}><Btn variant="outline" size="sm" style={full}>{t('einv.viewOnPortal')} ↗</Btn></a>}
        {status === 'valid' && <Btn variant="ghost" size="sm" onClick={() => setModal('cancel')} style={full}>{t('einv.cancel')}</Btn>}
      </div>

      {/* Submit */}
      <Modal open={modal === 'submit'} onClose={() => setModal('')} title={t('einv.modalTitle', { n: doc.docNumber })} width={520}>
        {!ready ? <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>{t('common.loading')}</p> : <>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: 12 }}>{t('einv.modalIntro')}</p>
          <div style={{ fontSize: 12.5, fontWeight: 600, padding: '8px 12px', borderRadius: 8, marginBottom: 14,
            background: ready.environment === 'production' ? 'rgba(239,68,68,0.08)' : 'rgba(245,158,11,0.10)',
            color: ready.environment === 'production' ? 'var(--danger)' : '#B45309' }}>
            {ready.environment === 'production' ? `⚠ ${t('einv.productionWarning')}` : `🧪 ${t('einv.sandboxWarning')}`}
          </div>

          {blocking.length > 0 ? <>
            <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>{t('einv.fixFirst')}</p>
            <ul style={{ margin: 0, paddingInlineStart: 18, fontSize: 13, color: 'var(--danger)', lineHeight: 1.7 }}>
              {blocking.map(p => <li key={p.code}>{t(`einvP.${p.code}`) === `einvP.${p.code}` ? p.message : t(`einvP.${p.code}`)}</li>)}
            </ul>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
              {fixSettings && <Btn variant="primary" onClick={() => navigate(blocking.some(p => /settings\.company/.test(p.field)) ? '/settings' : '/settings/einvoice')}>⚙ {t('einv.fixInSettings')}</Btn>}
              {fixClient && <Btn variant="outline" onClick={() => navigate('/clients')}>👥 {t('einv.fixInClient')}</Btn>}
              <Btn variant="ghost" onClick={() => setModal('')}>{t('common.cancel')}</Btn>
            </div>
          </> : <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Select label={t('einv.classification')} value={classificationCode} onChange={e => setClassificationCode(e.target.value)}>
                {codes.classifications.map(c => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
              </Select>
              {foreign && <Input label={t('einv.exchangeRate', { cur: doc.currency })} type="number" step="0.0001" min="0.0001" required
                value={exchangeRate} onChange={e => setExchangeRate(e.target.value)} style={{ maxWidth: 260 }} />}
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
              <Btn variant="ghost" onClick={() => setModal('')}>{t('common.cancel')}</Btn>
              <Btn variant="primary" onClick={submit} disabled={busy === 'submit' || needsRate}>{busy === 'submit' ? t('einv.submitting') : `🏛 ${t('einv.submit')}`}</Btn>
            </div>
          </>}
        </>}
      </Modal>

      {/* Cancel */}
      <Modal open={modal === 'cancel'} onClose={() => setModal('')} title={t('einv.cancelTitle')} width={480}>
        <form onSubmit={cancel} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6 }}>{t('einv.cancelIntro')}</p>
          <Textarea label={t('einv.cancelReason')} required maxLength={300} rows={3} placeholder={t('einv.cancelReasonPh')} value={reason} onChange={e => setReason(e.target.value)} />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Btn variant="ghost" type="button" onClick={() => setModal('')}>{t('common.cancel')}</Btn>
            <Btn variant="danger" type="submit" disabled={busy === 'cancel'}>{busy === 'cancel' ? t('common.saving') : t('einv.cancel')}</Btn>
          </div>
        </form>
      </Modal>
    </Card>
  );
}

const label = { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-subtle)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 };
const sandboxTag = { fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 10, background: 'rgba(245,158,11,0.15)', color: '#B45309', letterSpacing: '0.3px' };
const meta = { fontSize: 12, color: 'var(--text-muted)', marginTop: 6, overflowWrap: 'anywhere' };
const full = { width: '100%', justifyContent: 'center' };
